// Khung form phát lệnh dùng chung (N11 — gom lối chạy, 05/10/2026). Tách nguyên từ job-new.ts, GIỮ mọi data-testid cũ
// (pick-bot, pick-why, bundle, mode-dry/draft/live, kind-same/split, param-key/val, by-bot, list-param/text, gian-cach, canary-on,
// canary-bot, auto-approve, timeout, preview-info, warnings, form-error, submit-job).
//   dayDu: true  — form kỹ thuật đầy đủ (#/jobs/new, chỉ còn trong Chế độ kỹ thuật): chọn kịch bản, Chỉ dò trang (Super), chia đều,
//                  tham số theo bot, hết giờ, tự duyệt.
//   dayDu: false — cấu hình rút gọn cho trang "Kịch bản học trên bot" (#/kich-ban/tu-bot/:ten): kịch bản CỐ ĐỊNH, Chạy thử / Chạy thật,
//                  tham số có NHÃN lấy từ kho (bundle không có trong kho → ô tên=giá trị như cũ), giãn cách, thử 1 bot trước,
//                  "Tuỳ chọn nâng cao" (tự duyệt cấp lần chạy, chữ đỏ + hộp xác nhận). Hết giờ cố định 30 phút như Lịch chạy.
// Gọi /api/jobs/preview mỗi khi đổi → khoá bot không được phép + hiện cảnh báo. Phát xong → #/jobs/:id (Tiến độ lần chạy).
import { api, errMsg } from "../api";
import { busy, clear, confirmBox, debounce, h, toast } from "../dom";
import { chiaDeuText, modeTitle, parseListText } from "../format";
import { isSuper, store, upsertBot } from "../store";
import type { Bot, Job, JobSpec, PreviewResult } from "../types";
import type { BundleParam, KhoBundle } from "./bundles";
import { ganTenKbChu } from "../ten-kb"; // L-78: hiện TÊN kịch bản thay vì mã kb-…

interface ParamRow { k: HTMLInputElement; v: HTMLInputElement; tr: HTMLTableRowElement }

export interface KhungPhatLenhOpts {
  bots: Bot[];
  /** Bot chọn sẵn (đã lọc: chỉ mã có trong `bots`). */
  chonSan: string[];
  dayDu: boolean;
  alive: () => boolean;
  /** dayDu=false: tên bundle cố định (không có ô chọn kịch bản). */
  tenCoDinh?: string;
  /** dayDu=false: bản trong kho (tham số có nhãn; hash để ghi "có bản này / có bản khác"). null = chưa vào kho. */
  kho?: KhoBundle | null;
  /** Nút "Quay lại" cuối form. */
  quayLai?: { href: string; text: string; title: string };
}

export interface KhungPhatLenh {
  /** Điền sẵn từ spec (Ra lệnh → "Sửa trong form"). Gọi TRƯỚC batDau. */
  applyPrefill(sp: JobSpec): void;
  /** Vẽ lần đầu: danh sách kịch bản, bot chạy trước, gọi xem trước. */
  batDau(): void;
  cleanup(): void;
}

/** Tham số bundle có nhãn → chữ nhãn hiển thị. */
function nhanThamSo(k: string, p: BundleParam | undefined): string {
  return p?.label?.trim() ? p.label.trim() : k;
}

export function taoKhungPhatLenh(root: HTMLElement, opts: KhungPhatLenhOpts): KhungPhatLenh {
  const { bots, dayDu, alive } = opts;
  const coDinh = !dayDu ? (opts.tenCoDinh ?? "") : "";
  const kho = opts.kho ?? null;

  // ---- trạng thái form
  const picked = new Set<string>(opts.chonSan.filter((id) => bots.some((b) => b.id === id)));
  const locked = new Map<string, string>(); // botId → lý do từ preview
  let preview: PreviewResult | null = null;
  let previewSeq = 0;

  /** Rút gọn: bot đang có bản nào của kịch bản cố định. */
  function banTrenBot(b: Bot): { chu: string; cls: string; title: string } {
    const r = b.bundles.find((x) => x.name === coDinh);
    if (!r) return { chu: "chưa có", cls: "tag", title: "Bot chưa có kịch bản này — cài xuống ở thẻ “Cài xuống bot” bên dưới rồi mới chạy được" };
    if (kho && r.hash === kho.hash) return { chu: "có bản này", cls: "tag ok", title: `Bot có đúng bản v${kho.version} trong Kịch bản` };
    if (kho) return { chu: "có bản khác", cls: "tag warn", title: "Bot đang có bản khác cùng tên — chạy sẽ dùng bản trên bot; muốn dùng bản mới nhất hãy cài xuống lại" };
    return { chu: "có trên bot", cls: "tag ok", title: "Bot có kịch bản này (chưa đưa vào Kịch bản nên không so được bản)" };
  }

  // ---- chọn bot
  const botBoxes = new Map<string, { cb: HTMLInputElement; label: HTMLLabelElement; why: HTMLElement; choPhep: HTMLButtonElement | null }>();
  const botPick = h("div", { class: "bot-pick", "data-testid": "bot-pick" });
  for (const b of bots) {
    const cb = h("input", { type: "checkbox", checked: picked.has(b.id), disabled: !b.online, title: b.online ? "Tick = lệnh này sẽ chạy trên bot này" : "Bot ngoại tuyến — không chọn được", "data-testid": "pick-bot", dataset: { botId: b.id } });
    const why = h("span", { class: "small err", hidden: true, "data-testid": "pick-why" });
    const ban = !dayDu ? banTrenBot(b) : null;
    // Rút gọn (T22): bot chưa được phép chạy thật kịch bản này → nút "Cho phép chạy thật" ngay cạnh lý do (như trang Kịch bản sơ đồ).
    const choPhep = !dayDu ? h("button", { type: "button", class: "mini pri", hidden: true, "data-testid": "pick-cho-phep", title: "Thêm kịch bản này vào danh sách được chạy thật của bot (như nút Sửa ở trang Bot) — có hộp xác nhận" }, "Cho phép chạy thật") : null;
    const label = h("label", { class: b.online ? "" : "locked", title: b.online ? "Tick = lệnh này sẽ chạy trên bot này" : "Bot ngoại tuyến — không chọn được", dataset: { botId: b.id } }, cb,
      h("span", { class: "grow" }, b.name), b.online ? null : h("span", { class: "tag", title: "Bot đang không nối với máy chủ" }, "ngoại tuyến"), b.versionOld ? h("span", { class: "tag gold", title: "Bản bot (plugin) trên máy này cũ hơn máy khác — nên cập nhật" }, "bản cũ") : null,
      ban ? h("span", { class: ban.cls, title: ban.title, "data-testid": "pick-ban" }, ban.chu) : null, why, choPhep);
    cb.addEventListener("change", () => { if (cb.checked) picked.add(b.id); else picked.delete(b.id); onBotsChanged(); });
    choPhep?.addEventListener("click", (e) => {
      e.preventDefault(); // nút nằm trong <label>: không đảo ô tick
      void (async () => {
        const bot = store.bots.get(b.id) ?? b;
        const ok = await confirmBox({ title: `Cho phép chạy thật trên ${bot.name}?`, okText: "Cho phép", text: `Kịch bản “${coDinh}” được phép chạy THẬT trên ${bot.name}. Chỉ chạy khi bạn bấm Chạy; bước rủi ro cao vẫn dừng hỏi bạn duyệt.` });
        if (!ok || !alive()) return;
        await busy(choPhep, "Đang cho phép…", async () => {
          try {
            const nb = await api<Bot>("PATCH", `/api/bots/${encodeURIComponent(bot.id)}`, { liveAllowed: [...new Set([...bot.liveAllowed, coDinh])] });
            upsertBot(nb);
            toast(`Đã cho phép chạy thật “${coDinh}” trên ${nb.name}`);
            onChanged();
          } catch (err) { toast(errMsg(err), "err"); }
        });
      })();
    });
    botBoxes.set(b.id, { cb, label, why, choPhep });
    botPick.append(label);
  }
  if (!bots.length) botPick.append(h("p", { class: "muted" }, "Chưa có bot nào."));

  // ---- bundle (chỉ form đầy đủ)
  const bundleSel = h("select", { title: "Chọn kịch bản sẽ chạy; danh sách gộp từ các bot đã tick, kèm số bot có kịch bản đó", "data-testid": "bundle" });
  let keepBundle = ""; // kịch bản điền sẵn từ lệnh chữ — giữ trong danh sách dù bot đã tick chưa báo có
  function refreshBundles(): void {
    if (!dayDu) return;
    const cur = bundleSel.value;
    clear(bundleSel);
    const names = new Set<string>();
    if (keepBundle) names.add(keepBundle);
    for (const id of picked) for (const r of store.bots.get(id)?.bundles ?? []) names.add(r.name);
    const sorted = [...names].sort((a, b) => a.localeCompare(b, "vi"));
    bundleSel.append(h("option", { value: "" }, sorted.length ? "— chọn kịch bản —" : (picked.size ? "Bot đã tick chưa báo kịch bản nào" : "Tick bot trước")));
    for (const n of sorted) {
      const have = [...picked].filter((id) => store.bots.get(id)?.bundles.some((r) => r.name === n)).length;
      const o = h("option", { value: n }, have === picked.size ? n : `${n} (${have}/${picked.size} bot có)`);
      ganTenKbChu(o, n, (ten) => (have === picked.size ? ten : `${ten} — ${have}/${picked.size} bot có`)); // L-78 / N9: chỉ tên, không mã
      bundleSel.append(o);
    }
    bundleSel.value = sorted.includes(cur) ? cur : "";
  }
  const tenBundle = (): string => (dayDu ? bundleSel.value : coDinh);

  // ---- mode, kiểu
  // V7: "Chỉ dò trang" (dry) chỉ còn trong chế độ kỹ thuật (Super, form đầy đủ); người dùng thường mặc định Chạy thử.
  const kyThuat = dayDu && isSuper();
  const modeDry = h("input", { type: "radio", name: "mode", value: "dry", checked: kyThuat, title: modeTitle("dry"), "data-testid": "mode-dry" });
  // (08/10/2026, người dùng bỏ Nháp) KHÔNG còn lựa chọn "Chạy thử" — giữ phần tử ẩn (không gắn vào trang) cho mã cũ; mặc định Chạy thật.
  const modeDraft = h("input", { type: "radio", name: "mode", value: "draft", checked: false, title: modeTitle("draft"), "data-testid": "mode-draft" });
  const modeLive = h("input", { type: "radio", name: "mode", value: "live", checked: !kyThuat, title: modeTitle("live"), "data-testid": "mode-live" });
  const kindSame = h("input", { type: "radio", name: "kind", value: "giong_nhau", checked: true, title: "Mọi bot chạy cùng một bộ tham số", "data-testid": "kind-same" });
  const kindSplit = h("input", { type: "radio", name: "kind", value: "chia_deu", title: "Một danh sách được chia đều cho các bot, mỗi bot nhận một phần", "data-testid": "kind-split" });

  // ---- tham số chung (bảng key/value tự thêm dòng; chọn bundle có trong kho (gói F) → tự điền tên tham số + mặc định)
  const paramRows: ParamRow[] = [];
  let khoPromise: Promise<KhoBundle[]> | null = null;
  const loadKho = (): Promise<KhoBundle[]> => (khoPromise ??= api<KhoBundle[]>("GET", "/api/bundles").catch(() => [] as KhoBundle[]));
  async function fillParamsFromKho(): Promise<void> {
    const name = bundleSel.value;
    if (!name) return;
    // Chỉ bản trong kho của CHỦ các bot đã chọn (Super thấy kho mọi Boss — bundle cùng tên của Boss khác không phải kịch bản trên bot này).
    const chu = new Set([...picked].map((id) => store.bots.get(id)?.ownerId).filter((x): x is string => !!x));
    const kb = (await loadKho()).find((b) => b.name === name && (!chu.size || chu.has(b.ownerId)));
    if (!alive() || !kb || bundleSel.value !== name) return;
    const keys = Object.keys(kb.params ?? {});
    if (!keys.length) return;
    // Bỏ dòng trống, thêm tên tham số còn thiếu (giá trị = default của bundle); không đụng dòng người dùng đã gõ.
    const have = new Set(paramRows.map((r) => r.k.value.trim()).filter(Boolean));
    for (const r of [...paramRows]) if (!r.k.value.trim() && !r.v.value) { r.tr.remove(); paramRows.splice(paramRows.indexOf(r), 1); }
    let added = false;
    for (const k of keys) {
      if (have.has(k)) continue;
      const d = kb.params[k]?.default;
      addParamRow(k, d === undefined || d === null ? "" : String(d));
      added = true;
    }
    if (added) { refreshByBot(); onChanged(); }
  }
  const paramBody = h("tbody");
  function addParamRow(k = "", v = ""): void {
    const ki = h("input", { placeholder: "tên tham số", value: k, title: "Tên tham số kịch bản cần (ví dụ tu_khoa)", "data-testid": "param-key" });
    const vi = h("input", { placeholder: "giá trị", value: v, title: "Giá trị dùng chung cho mọi bot (có thể ghi đè riêng từng bot ở bảng bên dưới)", "data-testid": "param-val" });
    const tr = h("tr", {}, h("td", {}, ki), h("td", {}, vi),
      h("td", { class: "nowrap" }, h("button", { type: "button", class: "mini", title: "Bỏ dòng tham số này", onclick: () => { tr.remove(); paramRows.splice(paramRows.findIndex((r) => r.tr === tr), 1); refreshByBot(); onChanged(); } }, "×")));
    // Đổi TÊN tham số → cột bảng theo bot đổi; đổi giá trị thì không vẽ lại bảng (giữ focus ô đang gõ).
    ki.addEventListener("input", () => { refreshByBot(); onChanged(); });
    vi.addEventListener("input", onChanged);
    paramRows.push({ k: ki, v: vi, tr });
    paramBody.append(tr);
  }
  const paramTbl = h("div", { class: "tbl params" }, h("table", {}, h("thead", {}, h("tr", {}, h("th", {}, "Tham số"), h("th", {}, "Giá trị"), h("th", {}))), paramBody));
  const addParamBtn = h("button", { type: "button", class: "mini", title: "Thêm một dòng tham số trống", onclick: () => addParamRow() }, "+ thêm tham số");

  // ---- rút gọn: tham số có NHÃN từ kho (label / kiểu / mặc định / bắt buộc)
  const khoParams = !dayDu && kho ? Object.entries(kho.params ?? {}) : [];
  const nhanInputs = new Map<string, { inp: HTMLInputElement; p: BundleParam }>();
  const nhanBox = h("div", { class: "param-nhan", "data-testid": "param-nhan" });
  for (const [k, p] of khoParams) {
    const d = p.default;
    const so = p.type === "number";
    const inp = h("input", {
      value: d === undefined || d === null ? "" : String(d),
      inputmode: so ? "decimal" : undefined,
      placeholder: p.required ? "bắt buộc" : "để trống = không dùng",
      title: `Tham số ${k}${so ? " (số)" : ""}${p.required ? " — bắt buộc" : ""}`,
      "data-testid": "param-o", dataset: { key: k },
    });
    inp.addEventListener("input", onChanged);
    nhanInputs.set(k, { inp, p });
    nhanBox.append(h("label", { class: "f" }, h("span", {}, nhanThamSo(k, p), p.required ? h("b", { class: "err", title: "Bắt buộc" }, " *") : null, so ? h("span", { class: "small muted" }, " (số)") : null), inp));
  }
  if (dayDu || !khoParams.length) addParamRow();

  // ---- khác nhau theo bot (chỉ form đầy đủ)
  const byBotOn = h("input", { type: "checkbox", title: "Bật để nhập giá trị riêng cho từng bot; ô để trống sẽ dùng giá trị chung", "data-testid": "by-bot" });
  const byBotBody = h("tbody");
  const byBotHead = h("tr");
  const byBotTbl = h("div", { class: "tbl params", hidden: true, "data-testid": "by-bot-table" }, h("table", {}, h("thead", {}, byBotHead), byBotBody));
  const byBotVals = new Map<string, Map<string, HTMLInputElement>>(); // botId → key → input
  function refreshByBot(): void {
    byBotTbl.hidden = !byBotOn.checked;
    if (!byBotOn.checked) return;
    const keys = paramKeys();
    clear(byBotHead);
    byBotHead.append(h("th", {}, "Bot"), ...keys.map((k) => h("th", {}, k)));
    clear(byBotBody);
    for (const id of picked) {
      const b = store.bots.get(id);
      if (!b) continue;
      const m = byBotVals.get(id) ?? new Map<string, HTMLInputElement>();
      byBotVals.set(id, m);
      const tr = h("tr", {}, h("td", { class: "nowrap" }, h("span", { class: "wrap", style: "max-width:220px;display:inline-block" }, b.name)));
      for (const k of keys) {
        let inp = m.get(k);
        if (!inp) { inp = h("input", { placeholder: "(dùng chung)", title: `Giá trị riêng của ${b.name} cho tham số ${k}; để trống = dùng giá trị chung`, dataset: { botId: id, key: k } }); inp.addEventListener("input", onChanged); m.set(k, inp); }
        tr.append(h("td", {}, inp));
      }
      byBotBody.append(tr);
    }
    if (!keys.length) byBotBody.append(h("tr", {}, h("td", { class: "muted" }, "Thêm tham số chung trước để có cột.")));
  }
  byBotOn.addEventListener("change", () => { refreshByBot(); onChanged(); });

  // ---- chia_deu (chỉ form đầy đủ)
  const listParam = h("input", { placeholder: "tên tham số danh sách, ví dụ: tu_khoa", title: "Tên tham số mà mỗi bot sẽ nhận phần danh sách của mình", "data-testid": "list-param" });
  const listText = h("textarea", { placeholder: "mỗi dòng một phần tử", title: "Danh sách cần chia: mỗi dòng một phần tử; phần dư dồn cho bot đầu", "data-testid": "list-text" });
  const listInfo = h("p", { class: "small muted", "data-testid": "list-info" });
  const splitBox = h("div", { class: "card", hidden: true, "data-testid": "split-box" },
    h("label", { class: "f" }, h("span", {}, "Tham số nhận từng phần"), listParam),
    h("label", { class: "f" }, h("span", {}, "Danh sách (mỗi dòng một phần tử; phần dư dồn cho bot đầu)"), listText),
    listInfo);
  for (const i of [listParam, listText]) i.addEventListener("input", onChanged);

  // ---- giãn cách, canary, tự duyệt, timeout
  const gianCach = h("input", { type: "number", min: 0, step: 1, value: "180", title: "Mỗi bot bắt đầu lệch ngẫu nhiên trong khoảng 0 tới số giây này để không cùng lúc; 0 = tất cả bắt đầu ngay", "data-testid": "gian-cach" });
  const gianCachWarn = h("p", { class: "small warn", hidden: true, "data-testid": "gian-cach-warn" }, "Chạy thật với giãn cách 0 giây: mọi bot bắt đầu cùng lúc — trang web dễ nhận ra mẫu hành vi. Chỉ nên dùng cho trang nội bộ.");
  const canaryOn = h("input", { type: "checkbox", title: "Bật = chỉ 1 bot chạy trước; xong không lỗi thì máy chủ mới phát cho các bot còn lại", "data-testid": "canary-on" });
  const canaryBot = h("select", { title: "Bot sẽ chạy trước; \"ngẫu nhiên\" = máy chủ tự chọn", "data-testid": "canary-bot" });
  const autoApprove = h("input", { type: "checkbox", title: "Bật = bot tự làm bước rủi ro cao (gửi/đăng/xoá) không hỏi bạn; tắt = lần chạy dừng chờ bạn duyệt ở bước đó", "data-testid": "auto-approve" });
  const timeoutMin = h("input", { type: "number", min: 1, step: 1, value: "30", title: "Quá số phút này mà lần chạy chưa xong thì máy chủ đánh dấu Hết giờ và dừng", "data-testid": "timeout" });
  function refreshCanary(): void {
    const cur = canaryBot.value;
    clear(canaryBot);
    canaryBot.append(h("option", { value: "" }, "ngẫu nhiên"));
    for (const id of picked) canaryBot.append(h("option", { value: id }, store.bots.get(id)?.name ?? id));
    canaryBot.value = picked.has(cur) ? cur : "";
    canaryBot.disabled = !canaryOn.checked;
  }
  let modeTouchedCanary = false;
  for (const r of [modeDry, modeDraft, modeLive]) r.addEventListener("change", () => {
    if (!modeTouchedCanary) canaryOn.checked = modeLive.checked; // canary mặc định bật khi live
    refreshCanary(); onChanged();
  });
  canaryOn.addEventListener("change", () => { modeTouchedCanary = true; refreshCanary(); onChanged(); });
  for (const r of [kindSame, kindSplit]) r.addEventListener("change", () => { splitBox.hidden = !kindSplit.checked; onChanged(); });
  bundleSel.addEventListener("change", () => void fillParamsFromKho());
  for (const i of [gianCach, canaryBot, autoApprove, timeoutMin, bundleSel]) i.addEventListener(i === bundleSel || i === canaryBot ? "change" : "input", onChanged);
  // Rút gọn: tự duyệt cấp lần chạy nằm trong "Tuỳ chọn nâng cao" — tick phải xác nhận qua hộp (bundle cũ không có công tắc từng bước).
  if (!dayDu) autoApprove.addEventListener("change", () => {
    if (!autoApprove.checked) return;
    void confirmBox({
      title: "Tự duyệt bước rủi ro cao?",
      text: "Bot sẽ tự bấm gửi / đăng / xoá trong lần chạy này mà KHÔNG hỏi bạn. Chỉ bật khi chắc chắn nội dung đúng.",
      okText: "Bật tự duyệt", danger: true,
    }).then((ok) => { if (!ok) { autoApprove.checked = false; onChanged(); } });
  });

  // ---- preview + phát
  const warnBox = h("div", { class: "warn-box", hidden: true, "data-testid": "warnings" });
  const errBox = h("div", { class: "err-box", hidden: true, "data-testid": "form-error" });
  const previewInfo = h("p", { class: "small muted", "data-testid": "preview-info" });
  const submit = h("button", { type: "button", class: "pri", title: "Gửi lệnh này tới các bot đã chọn; bot sẽ chạy kịch bản theo tham số bên trên (bot bị khoá sẽ bị bỏ qua)", "data-testid": "submit-job" }, dayDu ? "Phát lệnh" : "Chạy");
  const quayLai = opts.quayLai ?? { href: "#/bots", text: "Quay lại", title: "Về trang Bot, bỏ form này (chưa phát gì)" };

  if (dayDu) {
    root.append(
      h("div", { class: "two" },
        h("div", {},
          h("div", { class: "card" },
            h("h3", {}, "1. Bot"),
            botPick,
            h("p", { class: "small muted" }, "Bot ngoại tuyến hoặc bị máy chủ từ chối (hết hạn mức, đang tạm dừng vì CAPTCHA, plugin cũ…) sẽ bị khoá kèm lý do.")),
          h("div", { class: "card" },
            h("h3", {}, "2. Kịch bản & chế độ"),
            h("label", { class: "f" }, h("span", {}, "Kịch bản (gộp từ các bot đã tick)"), bundleSel),
            h("div", { class: "row" },
              kyThuat ? h("label", { class: "inline", title: modeTitle("dry") }, modeDry, "Chỉ dò trang — chỉ tìm nút/ô trên trang, không thao tác (chế độ kỹ thuật)") : null,
              h("label", { class: "inline", title: modeTitle("live") }, modeLive, "Chạy — bot làm THẬT trên trang")),
            h("div", { class: "row", style: "margin-top:6px" },
              h("label", { class: "inline", title: "Mọi bot chạy cùng một bộ tham số" }, kindSame, "giống nhau — mọi bot cùng tham số"),
              h("label", { class: "inline", title: "Một danh sách được chia đều cho các bot, mỗi bot nhận một phần" }, kindSplit, "chia đều — một danh sách chia cho các bot"))),
          h("div", { class: "card" },
            h("h3", {}, "3. Tham số"),
            paramTbl,
            h("div", { class: "row", style: "margin-top:6px" }, addParamBtn, h("label", { class: "inline", title: "Bật để nhập giá trị riêng cho từng bot; ô để trống sẽ dùng giá trị chung" }, byBotOn, "Khác nhau theo bot")),
            byBotTbl,
            splitBox)),
        h("div", {},
          h("div", { class: "card" },
            h("h3", {}, "4. Chống mẫu hành vi"),
            h("label", { class: "f" }, h("span", {}, "Giãn cách tối đa giữa các bot (giây) — mỗi bot bắt đầu lệch ngẫu nhiên trong [0, T]"), gianCach),
            gianCachWarn,
            h("div", { class: "row" }, h("label", { class: "inline", title: "Bật = chỉ 1 bot chạy trước; xong không lỗi thì máy chủ mới phát cho các bot còn lại" }, canaryOn, "Chạy 1 bot trước: xong không lỗi mới phát cho các bot còn lại"), h("div", { style: "min-width:180px" }, canaryBot)),
            h("label", { class: "inline", style: "margin-top:8px", title: "Bật = bot tự làm bước rủi ro cao không hỏi bạn; tắt = lần chạy dừng chờ bạn duyệt ở bước đó" }, autoApprove, h("span", {}, "Tự duyệt bước rủi ro cao ", h("span", { class: "warn" }, "— bot sẽ bấm gửi/đăng/xoá mà không hỏi bạn"))),
            h("label", { class: "f", style: "margin-top:8px" }, h("span", {}, "Hết giờ (phút) cho mỗi lần chạy"), timeoutMin)),
          h("div", { class: "card" },
            h("h3", {}, "5. Kiểm tra & phát"),
            previewInfo, warnBox, errBox,
            h("div", { class: "row end" }, h("a", { class: "btn", href: quayLai.href, title: quayLai.title }, quayLai.text), submit)))));
  } else {
    root.append(
      h("div", { class: "khung-gon", "data-testid": "khung-phat-gon" },
        h("h3", {}, "Bot"),
        botPick,
        h("p", { class: "small muted" }, "Bot ngoại tuyến hoặc bị máy chủ từ chối (hết hạn mức, đang tạm dừng vì CAPTCHA, plugin cũ…) sẽ bị khoá kèm lý do."),
        h("p", { class: "small warn", "data-testid": "phat-lam-that" }, "Bot sẽ làm THẬT trên trang (đăng / bình luận / thích thật); bước rủi ro cao vẫn dừng chờ bạn duyệt."),
        h("h3", {}, "Tham số"),
        khoParams.length ? nhanBox : h("div", {}, paramTbl, h("div", { class: "row", style: "margin-top:6px" }, addParamBtn),
          h("p", { class: "small muted" }, "Kịch bản chưa đưa vào Kịch bản nên không có nhãn tham số — gõ tên = giá trị.")),
        h("h3", {}, "Nhiều bot"),
        h("label", { class: "f" }, h("span", {}, "Bắt đầu lệch nhau tối đa (giây) — 0 = cùng lúc"), gianCach),
        gianCachWarn,
        h("div", { class: "row" }, h("label", { class: "inline", title: "Bật = chỉ 1 bot chạy trước; xong không lỗi mới tới các bot còn lại" }, canaryOn, "Chạy 1 bot trước: xong không lỗi mới chạy các bot còn lại" /* đặc tả đổi 08/10/2026 (bỏ Nháp): trước "Thử trên 1 bot trước: …" */), h("div", { style: "min-width:180px" }, canaryBot)),
        h("details", { class: "nang-cao", "data-testid": "tuy-chon-nang-cao" },
          h("summary", { title: "Mở các tuỳ chọn ít dùng" }, "Tuỳ chọn nâng cao"),
          h("label", { class: "inline", style: "margin-top:8px", title: "Bật = bot tự làm bước rủi ro cao không hỏi bạn; tắt = lần chạy dừng chờ bạn duyệt ở bước đó" }, autoApprove, h("span", { class: "err" }, "Tự duyệt bước rủi ro cao — bot sẽ bấm gửi/đăng/xoá mà không hỏi bạn"))),
        previewInfo, warnBox, errBox,
        h("div", { class: "row end" }, h("a", { class: "btn", href: quayLai.href, title: quayLai.title }, quayLai.text), submit)));
  }

  function paramKeys(): string[] {
    return [...new Set(paramRows.map((r) => r.k.value.trim()).filter(Boolean))];
  }
  function collectParams(): Record<string, string> {
    const out: Record<string, string> = {};
    for (const r of paramRows) { const k = r.k.value.trim(); if (k) out[k] = r.v.value; }
    for (const [k, { inp }] of nhanInputs) if (inp.value !== "") out[k] = inp.value;
    return out;
  }
  function collectByBot(): Record<string, Record<string, string>> | undefined {
    if (!byBotOn.checked) return undefined;
    const out: Record<string, Record<string, string>> = {};
    for (const id of picked) {
      const m = byBotVals.get(id);
      if (!m) continue;
      const row: Record<string, string> = {};
      for (const [k, inp] of m) if (inp.value !== "" && paramKeys().includes(k)) row[k] = inp.value;
      if (Object.keys(row).length) out[id] = row;
    }
    return Object.keys(out).length ? out : undefined;
  }
  function buildSpec(): JobSpec {
    const spec: JobSpec = {
      kind: kindSplit.checked ? "chia_deu" : "giong_nhau",
      bundleName: tenBundle(),
      mode: modeLive.checked ? "live" : modeDraft.checked ? "draft" : "dry",
      botIds: [...picked],
      params: collectParams(),
      gianCachS: Math.max(0, Math.floor(Number(gianCach.value) || 0)),
      canary: { on: canaryOn.checked, ...(canaryOn.checked && canaryBot.value ? { botId: canaryBot.value } : {}) },
      autoApprove: autoApprove.checked,
      timeoutMin: Math.max(1, Math.floor(Number(timeoutMin.value) || 30)),
    };
    const byBot = collectByBot();
    if (byBot) spec.paramsByBot = byBot;
    if (spec.kind === "chia_deu") { spec.listParam = listParam.value.trim(); spec.list = parseListText(listText.value); }
    return spec;
  }
  function localErrors(spec: JobSpec): string[] {
    const errs: string[] = [];
    if (!spec.botIds.length) errs.push("Chưa tick bot nào");
    if (!spec.bundleName) errs.push("Chưa chọn kịch bản");
    if (spec.kind === "chia_deu") {
      if (!spec.listParam) errs.push("Chia đều: thiếu tên tham số danh sách");
      if (!spec.list?.length) errs.push("Chia đều: danh sách rỗng");
    }
    for (const [k, { inp, p }] of nhanInputs) {
      const v = inp.value.trim();
      if (p.required && !v) errs.push(`Chưa nhập “${nhanThamSo(k, p)}” (bắt buộc)`);
      else if (p.type === "number" && v && !Number.isFinite(Number(v))) errs.push(`“${nhanThamSo(k, p)}” phải là số`);
    }
    if (!Number.isFinite(Number(gianCach.value)) || Number(gianCach.value) < 0) errs.push("Giãn cách phải là số ≥ 0");
    else if (Number(gianCach.value) > 86_400) errs.push("Giãn cách tối đa 24 giờ"); // cùng giới hạn và chữ với máy chủ (jobs.ts) — khỏi gửi yêu cầu chắc chắn bị 400
    if (!Number.isFinite(Number(timeoutMin.value)) || Number(timeoutMin.value) < 1) errs.push("Hết giờ phải ≥ 1 phút");
    return errs;
  }

  const runPreview = debounce(300, async () => {
    const spec = buildSpec();
    const errs = localErrors(spec);
    if (errs.length) { previewInfo.textContent = errs.join(" · "); preview = null; applyLocks(); return; }
    const seq = ++previewSeq;
    previewInfo.textContent = "Đang kiểm tra với máy chủ…";
    try {
      const r = await api<PreviewResult>("POST", "/api/jobs/preview", spec);
      if (seq !== previewSeq || !alive()) return;
      preview = r;
      const okN = r.runs.filter((x) => x.ok).length;
      previewInfo.textContent = `${okN}/${r.runs.length} bot chạy được`;
      warnBox.hidden = !r.warnings.length;
      clear(warnBox);
      warnBox.append(h("ul", { style: "margin:0;padding-left:18px" }, ...r.warnings.map((w) => h("li", {}, w))));
      errBox.hidden = true;
    } catch (e) {
      if (seq !== previewSeq || !alive()) return;
      preview = null;
      previewInfo.textContent = "";
      errBox.textContent = `Không kiểm tra được: ${errMsg(e)}`;
      errBox.hidden = false;
    }
    applyLocks();
  });

  function applyLocks(): void {
    locked.clear();
    if (preview) for (const r of preview.runs) if (!r.ok) locked.set(r.botId, r.reason ?? "Máy chủ từ chối");
    for (const [id, box] of botBoxes) {
      const why = locked.get(id);
      const b = store.bots.get(id);
      const offline = !b?.online;
      box.cb.disabled = offline || !!why;
      box.label.classList.toggle("locked", offline || !!why);
      box.label.title = why ?? (offline ? "Bot ngoại tuyến — không chọn được" : "Tick = lệnh này sẽ chạy trên bot này");
      box.why.hidden = !why;
      box.why.textContent = why ? `— ${why}` : "";
      if (box.choPhep) box.choPhep.hidden = !(why && modeLive.checked && b && b.online && !b.liveAllowed.includes(coDinh));
    }
    const runnable = [...picked].filter((id) => !locked.has(id));
    submit.disabled = !runnable.length || !tenBundle() || localErrors(buildSpec()).length > 0;
  }

  function onBotsChanged(): void {
    refreshBundles();
    refreshCanary();
    refreshByBot();
    onChanged();
  }
  function onChanged(): void {
    gianCachWarn.hidden = !(modeLive.checked && Number(gianCach.value) === 0);
    if (kindSplit.checked) listInfo.textContent = chiaDeuText(parseListText(listText.value).length, picked.size);
    void runPreview();
  }

  submit.addEventListener("click", () => void busy(submit, "Đang phát…", async () => {
    const spec = buildSpec();
    // Bỏ bot bị khoá khỏi danh sách gửi (server cũng sẽ bỏ, nhưng gửi sạch cho rõ).
    spec.botIds = spec.botIds.filter((id) => !locked.has(id));
    if (spec.canary.botId && !spec.botIds.includes(spec.canary.botId)) delete spec.canary.botId;
    const errs = localErrors(spec);
    if (errs.length) { errBox.textContent = errs.join(" · "); errBox.hidden = false; return; }
    errBox.hidden = true;
    try {
      const job = await api<Job>("POST", "/api/jobs", spec);
      toast(`Đã phát lệnh cho ${job.runs.length} bot`);
      store.ticked.clear();
      location.hash = `/jobs/${encodeURIComponent(job.id)}`;
    } catch (e) {
      errBox.textContent = errMsg(e);
      errBox.hidden = false;
    }
  }));

  // ---- điền sẵn từ lệnh chữ (gói H)
  function applyPrefill(sp: JobSpec): void {
    picked.clear();
    for (const id of sp.botIds) if (bots.some((b) => b.id === id)) picked.add(id);
    for (const [id, box] of botBoxes) box.cb.checked = picked.has(id);
    keepBundle = sp.bundleName;
    refreshBundles();
    bundleSel.value = sp.bundleName;
    modeLive.checked = sp.mode === "live" || sp.mode === "draft"; modeDraft.checked = false; // lệnh chạy thử CŨ → phát lại là chạy thật (đã bỏ Nháp)
    modeDry.checked = sp.mode === "dry";
    kindSplit.checked = sp.kind === "chia_deu"; kindSame.checked = !kindSplit.checked;
    splitBox.hidden = !kindSplit.checked;
    for (const r of [...paramRows]) { r.tr.remove(); paramRows.splice(paramRows.indexOf(r), 1); }
    for (const [k, v] of Object.entries(sp.params)) addParamRow(k, v);
    if (!paramRows.length) addParamRow();
    if (sp.kind === "chia_deu") { listParam.value = sp.listParam ?? ""; listText.value = (sp.list ?? []).join("\n"); }
    gianCach.value = String(sp.gianCachS);
    canaryOn.checked = sp.canary.on; modeTouchedCanary = true;
    autoApprove.checked = sp.autoApprove;
    timeoutMin.value = String(sp.timeoutMin);
    refreshCanary();
    if (sp.canary.botId) canaryBot.value = sp.canary.botId;
  }

  return {
    applyPrefill,
    batDau: onBotsChanged,
    cleanup(): void { previewSeq++; },
  };
}
